import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Spin, notification } from 'antd';
import { useQuery, useMutation } from 'react-query';
import { Button, DynamicField, FieldTypes, type JsonDataItem, Space, Text } from '@g2u/ui-kit';
import DOMPurify from 'dompurify';

import { getSurvey, postSurvey } from 'queries';
import { normaliseData, validateFormSilently } from '../../utils';
import { SurveyLayout, SurveySection, SurveyResult } from 'components';
import Geeks2uLogoLarge from 'assets/images/geeks2u-logo-large.svg';
import styles from './dynamic-survey.module.scss';

export const DynamicSurvey: React.FC = () => {
  const { id, question, value } = useParams();
  const [form] = Form.useForm();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [isFormValid, setIsFromValid] = useState<boolean>(false);

  const { data: survey, isLoading, error: surveyLoadingError } = useQuery(['get-survey', id], getSurvey);

  useEffect(() => {
    if (question && value && question.startsWith('q_')) {
      form.setFieldValue(question.slice(2), value);
    }
  }, [question, value]);

  const {
    mutate: createSurvey,
    data: surveyResponse,
    isLoading: postSurveyLoading,
  } = useMutation(postSurvey, {
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: (error) => {
      notification.error({
        message: (error as JsonDataItem).errors.title,
        description: (error as JsonDataItem).errors.description,
      });
    },
  });

  const handleFormChange = () => {
    void validateFormSilently(form, setIsFromValid);
  };

  const handleFormSubmit = (values: JsonDataItem) => {
    createSurvey({
      variable: id,
      data: normaliseData(values),
    });
  };

  return (
    <SurveyLayout.$>
      {isLoading && <Spin size="large" fullscreen />}
      {surveyLoadingError && (
        <SurveyResult.$
          title={(surveyLoadingError as JsonDataItem).errors.title}
          description={(surveyLoadingError as JsonDataItem).errors.description}
          error
        />
      )}
      {submitted ? (
        <SurveyResult.$ title="Success" description={surveyResponse.complete_message} />
      ) : (
        survey && (
          <Form
            layout="vertical"
            form={form}
            onValuesChange={handleFormChange}
            onFinish={handleFormSubmit}
            autoComplete="off"
          >
            <Space.$
              size={Space.Size.Large}
              direction={Space.Direction.Vertical}
              align={Space.Align.Center}
              justify={Space.Justify.Center}
            >
              {survey.questions
                .filter((question: JsonDataItem) => Object.values(FieldTypes).includes(question.name))
                .sort((a: JsonDataItem, b: JsonDataItem) => a.position - b.position)
                .map((question: JsonDataItem, i: number) => {
                  return (
                    <SurveySection.$ key={i} dataTestId={`survey-section-${i}`}>
                      {i === 0 && (
                        <div className={styles.dynamicSurvey__logo}>
                          {' '}
                          <img
                            src={Geeks2uLogoLarge}
                            alt="geeks2u-survey-large-logo"
                            data-testid="header-section-logo"
                          />
                        </div>
                      )}
                      <DynamicField.$
                        form={form}
                        fieldConfig={{
                          name: question.ID,
                          label: (
                            <Text.$ size={Text.Size.XL}>
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(question.label),
                                }}
                              />
                            </Text.$>
                          ),
                          description: question.description ? (
                            <Text.$ type="secondary" size={Text.Size.LG}>
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(question.description),
                                }}
                              />
                            </Text.$>
                          ) : null,
                          required: question.required,
                          count: question.count,
                          options: question.options,
                          type: question.name,
                          placeholder: question.placeholder,
                          rules: question.required ? [{ required: true, message: 'required field' }] : [],
                          ...(question.name === FieldTypes.Select && {
                            fullWidth: true,
                          }),
                        }}
                      />
                    </SurveySection.$>
                  );
                })}
              <Button.$
                className={styles.dynamicSurvey__button}
                type={Button.Type.Primary}
                disabled={!isFormValid}
                onClick={() => {
                  form.submit();
                }}
                loading={postSurveyLoading}
                dataTestId="survey-submit-btn"
              >
                Submit
              </Button.$>
            </Space.$>
          </Form>
        )
      )}
    </SurveyLayout.$>
  );
};
